
import React from 'react';

import { TRANSLATIONS } from '../constants';
import { Language } from '../types';

interface HemiSyncAnimationProps {
  isActive: boolean;
  frequency: number;
  color: string;
  label: string;
  onInstructionsClick?: () => void;
  language: Language;
}

const HemiSyncAnimation: React.FC<HemiSyncAnimationProps> = ({ isActive, frequency, color, label, onInstructionsClick, language }) => {
  const t = TRANSLATIONS[language as keyof typeof TRANSLATIONS];
  // Calculate animation duration - decoupled from raw frequency for a more relaxing "breath" feel
  // but still influenced by it to show state difference
  const pulseDuration = isActive ? `${Math.max(4, 12 / frequency)}s` : '10s';
  const flowDuration = isActive ? `${Math.max(6, 20 / frequency)}s` : '20s';
  
  return (
    <div className="relative flex flex-col items-center justify-center w-full h-full min-h-[300px] md:min-h-[500px] overflow-hidden">
      {/* Central Label */}
      <div className="absolute top-0 z-20">
        <span 
          className="text-2xl md:text-6xl font-black italic tracking-tighter uppercase transition-all duration-1000"
          style={{ 
            color: color,
            textShadow: isActive ? `0 0 20px ${color}40` : 'none',
            opacity: 0.8
          }}
        >
          {label}
        </span>
      </div>

      {/* Toroidal Energy Field */}
      <div className="relative w-64 h-64 md:w-[500px] md:h-[500px] flex items-center justify-center">
        {/* Background Glow */}
        <div 
          className="absolute inset-0 rounded-full blur-[100px] opacity-20 transition-all duration-1000"
          style={{ backgroundColor: color }}
        />

        <svg viewBox="0 0 400 400" className="w-full h-full drop-shadow-2xl overflow-visible animate-slow-rotate">
          <defs>
            <radialGradient id="torusGradient" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor={color} stopOpacity="0.8" />
              <stop offset="100%" stopColor={color} stopOpacity="0" />
            </radialGradient>
            
            <filter id="glow">
              <feGaussianBlur stdDeviation="4" result="blur" />
              <feComposite in="SourceGraphic" in2="blur" operator="over" />
            </filter>
          </defs>

          {/* Toroidal Rings */}
          {[...Array(12)].map((_, i) => {
            const rotation = i * 30;
            return (
              <g key={i} transform={`rotate(${rotation} 200 200)`}>
                <ellipse 
                  cx="200" 
                  cy="200" 
                  rx={isActive ? 160 : 140} 
                  ry={isActive ? 60 : 40} 
                  fill="none" 
                  stroke={color} 
                  strokeWidth="0.5" 
                  className="torus-ring transition-all duration-1000"
                  style={{ 
                    opacity: isActive ? 0.3 : 0.1,
                    filter: isActive ? 'blur(1px)' : 'none',
                    animation: isActive 
                      ? `torus-flow ${flowDuration} linear infinite` 
                      : 'none',
                    animationDelay: `${i * -0.5}s`
                  }}
                />
                
                {/* Flowing Particles on the rings */}
                {isActive && (
                  <circle r="1.5" fill={color} filter="url(#glow)">
                    <animateMotion 
                      dur={`${flowDuration}`}
                      repeatCount="indefinite"
                      path={`M ${200-160},200 a 160,60 0 1,0 320,0 a 160,60 0 1,0 -320,0`}
                      begin={`${i * -0.8}s`}
                    />
                  </circle>
                )}
              </g>
            );
          })}

          {/* Central Core */}
          <circle 
            cx="200" 
            cy="200" 
            r={isActive ? 40 : 20} 
            fill={color} 
            className="transition-all duration-1000"
            style={{ 
              opacity: isActive ? 0.4 : 0.1,
              filter: `blur(${isActive ? '20px' : '10px'})`,
              animation: isActive ? `core-pulse ${pulseDuration} ease-in-out infinite` : 'none'
            }}
          />
          

        </svg>

        {/* Interactive Instructions Button */}
        <div className="absolute bottom-4 md:bottom-10 flex flex-col items-center gap-1 z-30">
           <button 
             onClick={onInstructionsClick}
             className={`group flex flex-col items-center gap-1 transition-all duration-500 hover:scale-110 active:scale-95 ${isActive ? 'animate-pulse' : 'opacity-60'}`}
           >
             <span className="text-[10px] md:text-[12px] font-black text-white uppercase tracking-[0.3em] whitespace-nowrap bg-blue-600 px-4 py-2 rounded-full border border-white/20 shadow-[0_0_20px_rgba(37,99,235,0.4)] backdrop-blur-sm">
               {t.monitor.instructions}
             </span>
           </button>
        </div>
      </div>

      <style>{`
        @keyframes torus-flow {
          0% { stroke-dashoffset: 400; opacity: 0.2; transform: scale(0.98); }
          50% { opacity: 0.5; transform: scale(1.02); }
          100% { stroke-dashoffset: 0; opacity: 0.2; transform: scale(0.98); }
        }
        @keyframes core-pulse {
          0%, 100% { transform: scale(1); opacity: 0.3; }
          50% { transform: scale(1.1); opacity: 0.6; }
        }
        @keyframes slow-rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-slow-rotate {
          animation: slow-rotate 60s linear infinite;
        }
        @keyframes ping-slow {
          0% { transform: scale(1); opacity: 0.6; }
          100% { transform: scale(4); opacity: 0; }
        }
        .animate-ping-slow {
          animation: ping-slow 5s cubic-bezier(0, 0, 0.2, 1) infinite;
        }
        .torus-ring {
          stroke-dasharray: 100 300;
        }
      `}</style>
    </div>
  );
};

export default HemiSyncAnimation;
